import * as React from "react";
import { cn } from "@/lib/utils";

export interface CounterPillProps {
  count: number;
  label: string;
  variant?: "info" | "warning";
  onClick?: () => void;
  className?: string;
  /** Override the default aria-label (`{count} {label}`). */
  "aria-label"?: string;
}

const variantClasses: Record<NonNullable<CounterPillProps["variant"]>, string> = {
  info: "border-border text-primary",
  warning: "border-warning/40 text-warning-foreground",
};

const dotVariantClasses: Record<NonNullable<CounterPillProps["variant"]>, string> = {
  info: "bg-primary text-primary-foreground",
  warning: "bg-warning text-warning-foreground",
};

export const CounterPill = React.forwardRef<HTMLElement, CounterPillProps>(
  ({ count, label, variant = "info", onClick, className, "aria-label": ariaLabel, ...rest }, ref) => {
    const computedAriaLabel = ariaLabel ?? `${count} ${label}`;
    const inner = (
      <>
        <span
          aria-hidden="true"
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
            dotVariantClasses[variant],
          )}
        >
          {count}
        </span>
        <span className="text-xs">{label}</span>
      </>
    );

    const base = cn(
      "inline-flex items-center gap-2 rounded-full border bg-card pl-1 pr-3 py-1",
      variantClasses[variant],
      className,
    );

    if (onClick) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={onClick}
          className={cn(base, "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
          aria-label={computedAriaLabel}
          {...rest}
        >
          {inner}
        </button>
      );
    }
    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={base} aria-label={computedAriaLabel} {...rest}>
        {inner}
      </div>
    );
  },
);
CounterPill.displayName = "CounterPill";
